import React from 'react';
import PropTypes from 'prop-types';

const WEEKDAY_HEADERS = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 일별 히트맵 차트 — 이번 달 매일 지출 금액을 달력 격자로 표시
 * 금액이 클수록 진한 빨간색 배경
 */
const HeatmapChart = React.memo(({ selectedMonth, transactions }) => {
  const [year, month] = selectedMonth.split('-').map(Number);
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  // 이번 달 거래에서 일별 지출 합산
  const dailyExpenses = {};
  transactions
    .filter(t => t.type === 'expense' && t.date.startsWith(selectedMonth))
    .forEach(t => {
      const day = new Date(t.date).getDate();
      dailyExpenses[day] = (dailyExpenses[day] || 0) + Number(t.amount);
    });

  const maxExpense = Math.max(...Object.values(dailyExpenses), 0);

  // 격자 셀 생성 (빈 셀 + 날짜 셀)
  const cells = [];
  for (let i = 0; i < firstDay; i++) {
    cells.push({ day: null, expense: 0 });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ day, expense: dailyExpenses[day] || 0 });
  }

  return (
    <div className="heatmap-container">
      {/* 요일 헤더 */}
      <div className="heatmap-grid heatmap-header">
        {WEEKDAY_HEADERS.map(header => (
          <div key={header} className="heatmap-cell heatmap-cell--header">
            {header}
          </div>
        ))}
      </div>

      {/* 날짜 격자 */}
      <div className="heatmap-grid">
        {cells.map((cell, index) => {
          if (cell.day === null) {
            return <div key={`empty-${index}`} className="heatmap-cell heatmap-cell--empty" />;
          }

          const opacity = maxExpense > 0 ? cell.expense / maxExpense : 0;
          const formattedAmount = cell.expense > 0
            ? `${cell.day}일: ${cell.expense.toLocaleString('ko-KR')}원`
            : `${cell.day}일: 지출 없음`;

          return (
            <div
              key={cell.day}
              className="heatmap-cell heatmap-cell--day"
              title={formattedAmount}
              aria-label={formattedAmount}
              style={{
                '--heatmap-opacity': opacity
              }}
            >
              <span className="heatmap-cell__date">{cell.day}</span>
              {cell.expense > 0 && (
                <span className="heatmap-cell__amount">
                  {cell.expense >= 10000
                    ? `${Math.round(cell.expense / 10000)}만`
                    : `${Math.round(cell.expense / 1000)}천`}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* 범례 */}
      <div className="heatmap-legend">
        <span className="heatmap-legend__label">적음</span>
        <div className="heatmap-legend__scale">
          {[0.1, 0.3, 0.5, 0.7, 0.9].map(level => (
            <div
              key={level}
              className="heatmap-legend__swatch"
              style={{ '--heatmap-opacity': level }}
            />
          ))}
        </div>
        <span className="heatmap-legend__label">많음</span>
      </div>
    </div>
  );
});

HeatmapChart.displayName = 'HeatmapChart';

HeatmapChart.propTypes = {
  selectedMonth: PropTypes.string.isRequired,
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
};

export default HeatmapChart;
